import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Document } from '@contentful/rich-text-types';
import { Heading, Paragraph } from 'components/Typography';
import { Button, IconEndAdornment } from 'components/Button';
import { DailyRoutineCtx, withDailyRoutineContext } from 'domains/dailyRoutines/context';
import { dailyRoutineStepId } from 'domains/dailyRoutines/steps';
import { UnorderedList } from 'components/Lists';
import { Card } from 'components/Card';
import { VideoPlayer } from 'components/VideoPlayer';
import { RichText } from 'domains/contentful/RichText/Text';
import { Accordion, AccordionItem } from '@expressable/syntax-ds';

const StrategyOverviewContent: React.FC = () => {
  const { dailyRoutineContent, learningPathContent, onNavigateToStep, previewMode } = useContext(DailyRoutineCtx);
  const { t } = useTranslation();

  const { introduction, tips, setting } = dailyRoutineContent;
  const {
    title: learningPathTitle,
    strategyOverviewSection: {
      fields: { overview: learningPathOverview, videoLink },
    },
    helpfulTipsSection: {
      fields: { tips: learningPathTips },
    },
  } = learningPathContent;

  const onContinue = () => onNavigateToStep(dailyRoutineStepId.submitActivity, true);

  return (
    <>
      <section>
        <Heading level="h3" useDefaultMargins>
          {t('dailyRoutine.practiceOverview.title', { setting })}
        </Heading>
        <RichText>{introduction as Document}</RichText>
      </section>

      <UnorderedList>
        {tips.map(tip => (
          <UnorderedList.Item key={tip}>
            <Heading level="h5" className="font-semibold">
              {tip}
            </Heading>
          </UnorderedList.Item>
        ))}
      </UnorderedList>

      <Button
        className="sm-d:w-full"
        size="lg"
        onClick={onContinue}
        endAdornment={<IconEndAdornment name="AngleRight" />}
        disabled={previewMode}
      >
        {t('dailyRoutine.practiceOverview.continue')}
      </Button>

      <Accordion title={t('dailyRoutine.practiceOverview.accordion.title')}>
        <AccordionItem title={learningPathTitle}>
          <Paragraph>{learningPathOverview}</Paragraph>

          {videoLink && <VideoPlayer videoLink={videoLink} className="mt-24" />}
        </AccordionItem>

        {learningPathTips.map(({ fields: { answer, question }, sys: { id } }) => (
          <AccordionItem title={question} key={id}>
            <Paragraph>{answer}</Paragraph>
          </AccordionItem>
        ))}
      </Accordion>

      <Card className="bg-yellow-300 w-full">
        <Heading level="h4" className="mb-40 sm-d:mb-32">
          {t('dailyRoutine.practiceOverview.feedbackCallout.text')}
        </Heading>

        <Button
          className="sm-d:w-full"
          size="lg"
          onClick={onContinue}
          endAdornment={<IconEndAdornment name="AngleRight" />}
          disabled={previewMode}
        >
          {t('dailyRoutine.practiceOverview.feedbackCallout.button')}
        </Button>
      </Card>
    </>
  );
};

export default withDailyRoutineContext(StrategyOverviewContent, dailyRoutineStepId.overview);
